// The drag handle on the left edge of the right-hand dock.
// Mouse, pen and touch: pointer events with pointer capture. Keyboard: the arrow keys (Shift = 4 times as far).
// Double-click: back to the default width. The numbers live in dockWidth.ts.
//
// While the pointer is down the width is shown at once through onPreview (no render of the app), and kept
// through onCommit when the pointer is released. Escape or a cancelled pointer puts the old width back.
import { useRef } from "preact/hooks";
import { DOCK_DEFAULT, dockMax, dragDockWidth, stepDockWidth } from "./dockWidth";

export interface DockHandleProps {
  /** The width now, already limited to the window. */
  width: number;
  windowWidth: number;
  onPreview(width: number): void;
  onCommit(width: number): void;
  onReset(): void;
}

interface Drag {
  id: number;
  startX: number;
  startW: number;
  live: number;
}

export function DockHandle({ width, windowWidth, onPreview, onCommit, onReset }: DockHandleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const current = useRef(width);
  current.current = drag.current ? drag.current.live : width;

  const show = (w: number) => {
    current.current = w;
    ref.current?.setAttribute("aria-valuenow", String(w));
    onPreview(w);
  };

  const finish = (keep: boolean) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    document.body.classList.remove("dock-dragging");
    try {
      ref.current?.releasePointerCapture(d.id);
    } catch {
      /* the pointer is already gone */
    }
    if (keep) onCommit(d.live);
    else show(d.startW);
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault(); // no text selection, no scroll start
    ref.current?.focus();
    try {
      ref.current?.setPointerCapture(e.pointerId);
    } catch {
      /* a synthetic pointer cannot be captured: the move events still arrive on the handle */
    }
    drag.current = { id: e.pointerId, startX: e.clientX, startW: current.current, live: current.current };
    document.body.classList.add("dock-dragging");
  };

  const onPointerMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    const w = dragDockWidth(d.startW, d.startX, e.clientX, window.innerWidth);
    if (w === d.live) return;
    d.live = w;
    show(w);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && drag.current) {
      e.preventDefault();
      e.stopPropagation();
      finish(false);
      return;
    }
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    // These keys belong to the handle: they must not move the selected objects.
    e.preventDefault();
    e.stopPropagation();
    if (drag.current) return;
    const next = stepDockWidth(current.current, e.key === "ArrowLeft" ? -1 : 1, e.shiftKey, window.innerWidth);
    if (next !== current.current) {
      current.current = next;
      onCommit(next);
    }
  };

  return (
    <div
      ref={ref}
      class="dock-resize"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the panels"
      aria-valuenow={width}
      aria-valuemin={DOCK_DEFAULT}
      aria-valuemax={dockMax(windowWidth)}
      tabIndex={0}
      title="Drag to resize the panels. Double-click to reset. Arrow keys: 16 px, with Shift 64 px."
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => finish(true)}
      onPointerCancel={() => finish(false)}
      onLostPointerCapture={() => finish(true)}
      onKeyDown={onKeyDown}
      onDblClick={(e) => {
        e.preventDefault();
        onReset();
      }}
    />
  );
}
